/* eslint-disable  @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { AbstractDataProvider } from '@epicuro-next/data-providers/base-data-provider';
import { ensureArray } from '@epicuro-next/utilities';
import { createSelector, Store } from '@ngrx/store';
import { combineLatest, Observable, take } from 'rxjs';

type GlobalStoreProviderServiceArgs = {
  path?: string;
  paths?: string[];
  once?: boolean;
};
@Injectable()
export class GlobalStoreProviderService implements AbstractDataProvider {
  constructor(private readonly store: Store) {}

  public read<T>(
    pathOrPathsOrArgs: string | string[] | GlobalStoreProviderServiceArgs,
  ): Observable<T> {
    const { paths, once } = this.getPathsAndOptions(pathOrPathsOrArgs);

    const dynamicSelectors: Observable<T>[] =
      this.createDynamicSelectorsForPaths<T>(paths, once);

    if (dynamicSelectors.length === 1) {
      return dynamicSelectors[0];
    } else {
      return combineLatest(dynamicSelectors) as Observable<T>;
    }
  }

  private getPathsAndOptions(
    pathOrPathsOrArgs: string | string[] | GlobalStoreProviderServiceArgs,
  ): { paths: string[]; once: boolean } {
    let paths: string[];
    let once = false;
    if (
      typeof pathOrPathsOrArgs === 'object' &&
      !Array.isArray(pathOrPathsOrArgs)
    ) {
      const args = pathOrPathsOrArgs as GlobalStoreProviderServiceArgs;
      paths = ensureArray(args.paths ?? args.path ?? []);
      if (args.once !== undefined) {
        once = Boolean(args.once);
      }
    } else {
      paths = ensureArray(pathOrPathsOrArgs);
    }
    return { paths, once };
  }

  private createDynamicSelectorsForPaths<T>(paths: string[], once: boolean) {
    const dynamicSelectors: Observable<T>[] = [];
    for (const path of paths) {
      const pathParts = path.split('.');
      const dynamicSelector = this.createDynamicSelectorFromPaths(pathParts);
      let obs$ = this.store.select(dynamicSelector);
      if (once) {
        obs$ = obs$.pipe(take(1));
      }
      dynamicSelectors.push(obs$);
    }
    return dynamicSelectors;
  }

  private createDynamicSelectorFromPaths(paths: string[]) {
    return createSelector(
      (state) => state,
      // tslint:disable-next-line: no-any
      (state: any) => {
        let currentState = state;
        for (const path of paths) {
          if (currentState) {
            currentState = currentState[path];
          } else {
            return undefined;
          }
        }
        return currentState;
      },
    );
  }

  public write() {
    // for linter
  }
}
